import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVenues } from '../contexts/VenueContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';

const { 
  FiHome, FiGrid, FiCalendar, FiCreditCard, FiSettings, 
  FiLogOut, FiUser, FiCode, FiBriefcase, FiChevronDown, FiChevronRight 
} = FiIcons;

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { venues } = useVenues();
  const location = useLocation();
  const navigate = useNavigate();
  const [venuesOpen, setVenuesOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: FiGrid },
    { name: 'Reservations', href: '/reservations', icon: FiCalendar },
    { name: 'Widget Settings', href: '/widget-settings', icon: FiCode },
    { name: 'Subscription', href: '/subscription', icon: FiCreditCard },
    { name: 'Bank Settings', href: '/bank-settings', icon: FiBriefcase },
    { name: 'Settings', href: '/settings', icon: FiSettings }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <Logo />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}

            {/* Dynamic Venue List */}
            <div className="pt-4">
              <button 
                onClick={() => setVenuesOpen(!venuesOpen)}
                className="w-full flex items-center justify-between px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
              >
                My Venues
                <SafeIcon icon={venuesOpen ? FiChevronDown : FiChevronRight} className="h-3 w-3" />
              </button>
              
              {venuesOpen && (
                <div className="mt-1 space-y-1">
                  {venues.map((venue) => {
                    const isActive = location.pathname === `/venue-pricing/${venue.id}`;
                    return (
                      <Link
                        key={venue.id}
                        to={`/venue-pricing/${venue.id}`}
                        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors pl-8 ${
                          isActive 
                            ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <SafeIcon icon={FiHome} className="mr-3 h-4 w-4" />
                        <span className="truncate">{venue.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* User Menu */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center mb-4 px-2">
              <div className="bg-blue-100 p-2 rounded-full">
                <SafeIcon icon={FiUser} className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3 overflow-hidden">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="mr-3 h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-64 min-h-screen">
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;